import { useLiveQuery } from '@tanstack/react-db'

import { getAllowedCalloutStatusTransitions } from '@/db/domain/calloutStatus'
import { getAllowedJobStatusTransitions } from '@/db/domain/jobStatus'
import { getAllowedSessionStatusTransitions } from '@/db/domain/sessionStatus'
import { statusIsBlocking } from '@/db/domain/statusRules'
import type { User } from '@/db/domain/models'
import { formatServiceStatus } from '@/help/serviceStatus'
import { useDb } from '@/lib/dbContext'
import { fromUtcIsoToZaParts } from '@/lib/datetime'
import {
  getCurrentBlockedReason,
  getCurrentJobAssignment,
  getCurrentUnsuccessfulReason,
} from '@/lib/jobAssignments'
import { getCurrentAssignment } from '@/lib/jobAssignments'
import { normalizeJobNumberTag } from '@/lib/jobNumberTag'
import type {
  Callout,
  Job,
  JobAssignment,
  Note,
  ServiceAssignment,
  StatusChangeLog,
} from '@/lib/schemas/db'
import { Sentry } from '@/lib/sentry'
import type {
  ServiceActivityEntry,
  ServiceItem,
  ServiceType,
} from '@/screens/ServicesScreen'

const missingPublicIdWarnings = new Set<string>()

function requirePublicId(resourceType: string, id: string, publicId?: string) {
  if (publicId && publicId.trim().length > 0) return publicId

  const key = `${resourceType}:${id}`
  if (!missingPublicIdWarnings.has(key)) {
    missingPublicIdWarnings.add(key)
    const message = `Missing publicId for ${resourceType} row: ${id}`
    console.error(message)
    Sentry.captureMessage(message, {
      level: 'error',
      extra: { resourceType, id },
    })
  }

  return `Missing public ID (${id})`
}

function groupByServiceId<T extends { serviceId: string }>(rows: T[]) {
  const map = new Map<string, T[]>()
  for (const row of rows) {
    const current = map.get(row.serviceId) ?? []
    current.push(row)
    map.set(row.serviceId, current)
  }
  return map
}

/** Normalises PB's space-separated timestamps so string sorting is stable. */
function sortKey(timestamp: string) {
  return timestamp.replace(' ', 'T')
}

function buildActivity(
  createdAt: string,
  authorId: string | undefined,
  statusChanges: StatusChangeLog[],
  notes: Note[],
  getAuthorDisplayName: (authorId: string) => string,
): ServiceActivityEntry[] {
  const who = (id?: string) => (id ? getAuthorDisplayName(id) : 'Front desk')

  const entries: ServiceActivityEntry[] = [
    {
      id: 'created',
      kind: 'create',
      createdAt,
      who: who(authorId),
      action: 'Service created',
    },
    ...statusChanges.map(
      (change): ServiceActivityEntry => ({
        id: `status-${change.id}`,
        kind: statusIsBlocking(change.newStatus)
          ? 'block'
          : change.newStatus === 'Invoiced'
            ? 'invoice'
            : 'status',
        createdAt: change.createdAt,
        occurredAt: change.occurredAt,
        who: who(change.authorId),
        action: `Status → ${formatServiceStatus(change.newStatus)}`,
        reason: change.reason,
      }),
    ),
    ...notes.map(
      (note): ServiceActivityEntry => ({
        id: `note-${note.id}`,
        kind: 'note',
        createdAt: note.createdAt,
        who: who(note.authorId),
        text: note.text || note.caption,
        photoUrl: note.photoUrl,
        photoUrls: note.photoUrls,
        noteId: note.id,
        isPhoto: Boolean(note.photoUrl || note.photo),
        noteDeleted: note.deletedAt
          ? {
              at: note.deletedAt,
              byName: note.deletedBy ? who(note.deletedBy) : undefined,
            }
          : undefined,
      }),
    ),
  ]

  // Oldest first, newest at the bottom — the same reading order as the Log
  // Work feed and the follow-up panel thread.
  return entries.sort((a, b) =>
    sortKey(a.createdAt).localeCompare(sortKey(b.createdAt)),
  )
}

export function isArchivedService(item: ServiceItem): boolean {
  return item.status === 'Invoiced' || item.status === 'Cancelled'
}

export interface ServiceItemsResult {
  isLoading: boolean
  isError: boolean
  services: ServiceItem[]
  users: Array<{ id: string; name: string }>
}

/**
 * Assembles every callout, session and job into unified ServiceItems with
 * status history, notes activity and job assignments resolved.
 */
export function useServiceItems(): ServiceItemsResult {
  const db = useDb()

  const calloutsResult = useLiveQuery((q) => q.from({ callouts: db.calloutsCollection }))
  const sessionsResult = useLiveQuery((q) => q.from({ sessions: db.sessionsCollection }))
  const jobsResult = useLiveQuery((q) => q.from({ jobs: db.jobsCollection }))
  const calloutStatusChangesResult = useLiveQuery((q) =>
    q.from({ changes: db.statusChangesCollection }),
  )
  const sessionStatusChangesResult = useLiveQuery((q) =>
    q.from({ changes: db.sessionStatusChangesCollection }),
  )
  const jobStatusChangesResult = useLiveQuery((q) =>
    q.from({ changes: db.jobStatusChangesCollection }),
  )
  const calloutNotesResult = useLiveQuery((q) => q.from({ notes: db.notesCollection }))
  const sessionNotesResult = useLiveQuery((q) =>
    q.from({ notes: db.sessionNotesCollection }),
  )
  const jobNotesResult = useLiveQuery((q) => q.from({ notes: db.jobNotesCollection }))
  const customersResult = useLiveQuery((q) => q.from({ customers: db.customersCollection }))
  const assignmentsResult = useLiveQuery((q) =>
    q.from({ assignments: db.jobAssignmentsCollection }),
  )
  const calloutAssignmentsResult = useLiveQuery((q) =>
    q.from({ assignments: db.calloutAssignmentsCollection }),
  )
  const sessionAssignmentsResult = useLiveQuery((q) =>
    q.from({ assignments: db.sessionAssignmentsCollection }),
  )
  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))

  const results = [
    calloutsResult,
    sessionsResult,
    jobsResult,
    calloutStatusChangesResult,
    sessionStatusChangesResult,
    jobStatusChangesResult,
    calloutNotesResult,
    sessionNotesResult,
    jobNotesResult,
    customersResult,
    assignmentsResult,
    calloutAssignmentsResult,
    sessionAssignmentsResult,
    usersResult,
  ]

  if (results.some((result) => result.isLoading || result.isError)) {
    return {
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
      services: [],
      users: [],
    }
  }

  const customerById = new Map(
    (customersResult.data ?? []).map((customer) => [customer.id, customer]),
  )
  const usersById = new Map<string, User>()
  for (const user of (usersResult.data ?? []) as User[]) {
    usersById.set(user.id, user)
  }

  const calloutChangesById = groupByServiceId(
    (calloutStatusChangesResult.data ?? []) as StatusChangeLog[],
  )
  const sessionChangesById = groupByServiceId(
    (sessionStatusChangesResult.data ?? []) as StatusChangeLog[],
  )
  const jobChangesById = groupByServiceId(
    (jobStatusChangesResult.data ?? []) as StatusChangeLog[],
  )
  const calloutNotesById = groupByServiceId((calloutNotesResult.data ?? []) as Note[])
  const sessionNotesById = groupByServiceId((sessionNotesResult.data ?? []) as Note[])
  const jobNotesById = groupByServiceId((jobNotesResult.data ?? []) as Note[])

  const assignmentsByJobId = new Map<string, JobAssignment[]>()
  for (const assignment of (assignmentsResult.data ?? []) as JobAssignment[]) {
    const current = assignmentsByJobId.get(assignment.jobId) ?? []
    current.push(assignment)
    assignmentsByJobId.set(assignment.jobId, current)
  }
  const serviceAssignmentsById = (rows: ServiceAssignment[]) => {
    const map = new Map<string, ServiceAssignment[]>()
    for (const assignment of rows) {
      const current = map.get(assignment.serviceId) ?? []
      current.push(assignment)
      map.set(assignment.serviceId, current)
    }
    return map
  }
  const calloutAssignmentsById = serviceAssignmentsById(
    (calloutAssignmentsResult.data ?? []) as ServiceAssignment[],
  )
  const sessionAssignmentsById = serviceAssignmentsById(
    (sessionAssignmentsResult.data ?? []) as ServiceAssignment[],
  )

  const bookingToItem = (
    record: Callout,
    type: Exclude<ServiceType, 'job'>,
    statusChanges: StatusChangeLog[],
    notes: Note[],
    detailsTo: ServiceItem['detailsTo'],
  ): ServiceItem => {
    const { day, time } = fromUtcIsoToZaParts(record.startTime)
    const customer = customerById.get(record.customerId)
    const currentAssignment = getCurrentAssignment(
      (type === 'callout' ? calloutAssignmentsById : sessionAssignmentsById).get(
        record.id,
      ) ?? [],
      usersById,
    )
    return {
      key: `${type}:${record.id}`,
      type,
      id: requirePublicId(type, record.id, record.publicId),
      privateId: record.id,
      customerName: record.contactName ?? customer?.fullName ?? '',
      customerId: record.customerId,
      customerPublicId: customer?.publicId,
      summary: record.issueSummary ?? '',
      bookedDate: day,
      bookedTime: record.isWholeDay ? '' : time,
      bookedSort: record.startTime,
      status: record.status,
      statusChanges,
      statusTransitions:
        type === 'callout'
          ? getAllowedCalloutStatusTransitions(record.status)
          : getAllowedSessionStatusTransitions(record.status),
      assignedUserId: currentAssignment?.assignment.userId,
      assignedUserName: currentAssignment
        ? currentAssignment.user?.name || currentAssignment.user?.email
        : undefined,
      phone: record.contactPhone,
      updatedAt: record.updatedAt,
      activity: buildActivity(
        record.createdAt,
        (record as Callout & { authorId?: string }).authorId,
        statusChanges,
        notes,
        db.getAuthorDisplayName,
      ),
      detailsTo,
    }
  }

  const jobToItem = (job: Job): ServiceItem => {
    const customer = customerById.get(job.customerId)
    const statusChanges = jobChangesById.get(job.id) ?? []
    const notes = jobNotesById.get(job.id) ?? []
    const currentAssignment = getCurrentJobAssignment(
      assignmentsByJobId.get(job.id) ?? [],
      usersById,
    )
    const { day } = fromUtcIsoToZaParts(job.createdAt)
    return {
      key: `job:${job.id}`,
      type: 'job',
      id: job.publicId?.trim() || 'Pending…',
      privateId: job.id,
      customerName: customer?.fullName ?? job.contactName ?? 'Unknown customer',
      customerId: job.customerId,
      customerPublicId: customer?.publicId,
      summary: job.fault,
      bookedDate: day,
      bookedTime: '',
      bookedSort: job.createdAt,
      status: job.status,
      statusChanges,
      statusTransitions: getAllowedJobStatusTransitions(job.status),
      blockedReason: getCurrentBlockedReason(job.status, statusChanges),
      unsuccessfulReason: getCurrentUnsuccessfulReason(job.status, statusChanges),
      numberTag: normalizeJobNumberTag(job.numberTag) || undefined,
      assignedUserId: currentAssignment?.assignment.userId,
      assignedUserName: currentAssignment?.user?.name,
      phone: job.contactPhone ?? customer?.contactPhone,
      updatedAt: job.updatedAt,
      activity: buildActivity(
        job.createdAt,
        job.authorId,
        statusChanges,
        notes,
        db.getAuthorDisplayName,
      ),
      detailsTo: '/jobs/$id',
    }
  }

  const services: ServiceItem[] = [
    ...(calloutsResult.data ?? []).map((callout: Callout) =>
      bookingToItem(
        callout,
        'callout',
        calloutChangesById.get(callout.id) ?? [],
        calloutNotesById.get(callout.id) ?? [],
        '/callouts/$id',
      ),
    ),
    ...(sessionsResult.data ?? []).map((session: Callout) =>
      bookingToItem(
        session,
        session.sessionSubtype === 'Sit-down Session' ? 'sitdown' : 'remote',
        sessionChangesById.get(session.id) ?? [],
        sessionNotesById.get(session.id) ?? [],
        '/sessions/$id',
      ),
    ),
    ...(jobsResult.data ?? []).map((job: Job) => jobToItem(job)),
  ]

  const users = ((usersResult.data ?? []) as User[])
    .map((user) => ({ id: user.id, name: user.name || user.email }))
    .sort((a, b) => a.name.localeCompare(b.name))

  return { isLoading: false, isError: false, services, users }
}
